import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Loader2, ArrowRight, Check, File } from 'lucide-react';
import { mergeData, previewMerge, uploadSecondFile, getInfo } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import MissingChoice from './MissingChoice';

const MergeOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [step, setStep] = useState(1); // 1: upload, 2: select join columns, 3: select output columns
  const [secondFile, setSecondFile] = useState(null);
  const [secondFileInfo, setSecondFileInfo] = useState(null);
  const [columns, setColumns] = useState([]);
  const [secondColumns, setSecondColumns] = useState([]);
  
  // Step 2: Join columns
  const [leftJoinCol, setLeftJoinCol] = useState('');
  const [rightJoinCol, setRightJoinCol] = useState('');
  const [joinType, setJoinType] = useState('left');
  
  // Step 3: Output columns selection
  const [selectedLeftCols, setSelectedLeftCols] = useState([]);
  const [selectedRightCols, setSelectedRightCols] = useState([]);
  
  const [loading, setLoading] = useState(false);
  // Ce qui joindrait, avant de joindre. Le compte de lignes d'une jointure à
  // gauche est celui du fichier de départ quoi qu'il arrive : sans ça, rien à
  // l'écran ne distingue une jointure réussie d'une qui n'apparie rien.
  const [report, setReport] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const loadColumns = async () => {
      try {
        const result = await getInfo();
        setColumns(result.info.column_names || []);
      } catch (error) {
        toast.error(t('form.loadColumnsFailed'));
      }
    };
    loadColumns();
  }, []);

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const result = await uploadSecondFile(file);
      setSecondFile(file);
      setSecondFileInfo(result.info);
      const secCols = result.info.column_names || [];
      setSecondColumns(secCols);
      
      // Auto-detect matching column
      const matchingCols = columns.filter(col => secCols.includes(col));
      if (matchingCols.length > 0) {
        setLeftJoinCol(matchingCols[0]);
        setRightJoinCol(matchingCols[0]);
      } else if (columns.length > 0 && secCols.length > 0) {
        setLeftJoinCol(columns[0]);
        setRightJoinCol(secCols[0]);
      }
      
      // Pre-select all columns by default
      setSelectedLeftCols(columns);
      
      // For right file, select all EXCEPT the join column if it matches the left join column
      // This avoids duplicate join columns in the result
      if (matchingCols.length > 0) {
        // If join column is the same in both files, exclude it from right selection
        setSelectedRightCols(secCols.filter(col => !matchingCols.includes(col)));
      } else {
        setSelectedRightCols(secCols);
      }
      
      toast.success(t('merge.uploaded'));
      // Same as the first upload: skipped rows, a separator guess or ignored
      // sheets have to be visible here too, not only on the first file.
      (result.warnings || []).forEach((warning) => toast.warning(warning, { duration: 8000 }));
      setStep(2); // Move to column selection
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || t('upload.failed');
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    await handleFileUpload(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1,
    multiple: false,
    noClick: false,
    noKeyboard: false
  });

  const toggleLeftCol = (col) => {
    if (selectedLeftCols.includes(col)) {
      setSelectedLeftCols(selectedLeftCols.filter(c => c !== col));
    } else {
      setSelectedLeftCols([...selectedLeftCols, col]);
    }
  };

  const toggleRightCol = (col) => {
    if (selectedRightCols.includes(col)) {
      setSelectedRightCols(selectedRightCols.filter(c => c !== col));
    } else {
      setSelectedRightCols([...selectedRightCols, col]);
    }
  };

  const selectAllLeft = () => setSelectedLeftCols(columns);
  const deselectAllLeft = () => setSelectedLeftCols([]);
  const selectAllRight = () => setSelectedRightCols(secondColumns);
  const deselectAllRight = () => setSelectedRightCols([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!leftJoinCol || !rightJoinCol) {
      toast.error(t('merge.needJoinColumns'));
      return;
    }
    
    if (selectedLeftCols.length === 0 && selectedRightCols.length === 0) {
      toast.error(t('merge.needOutputColumns'));
      return;
    }
    
    setLoading(true);
    try {
      const config = {
        left_on: [leftJoinCol],
        right_on: [rightJoinCol],
        how: joinType,
        suffixes: ['_left', '_right'],
        selected_left_columns: selectedLeftCols,
        selected_right_columns: selectedRightCols
      };
      
      const result = await mergeData(config);
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('merge.failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!leftJoinCol || !rightJoinCol) {
      setReport(null);
      return undefined;
    }
    let current = true;
    previewMerge({ left_on: [leftJoinCol], right_on: [rightJoinCol], how: joinType })
      .then((result) => {
        // Une réponse lente ne doit pas écraser un choix plus récent.
        if (current) setReport(result.report);
      })
      .catch(() => {
        if (current) setReport(null);
      });
    return () => { current = false; };
  }, [leftJoinCol, rightJoinCol, joinType]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-6">
        <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-white' : 'bg-muted'}`}>
            {step > 1 ? <Check className="h-5 w-5" /> : '1'}
          </div>
          <span className="text-sm font-medium">{t('merge.stepUpload')}</span>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
        <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-white' : 'bg-muted'}`}>
            {step > 2 ? <Check className="h-5 w-5" /> : '2'}
          </div>
          <span className="text-sm font-medium">{t('merge.stepJoin')}</span>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
        <div className={`flex items-center gap-2 ${step >= 3 ? 'text-primary' : 'text-muted-foreground'}`}>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-primary text-white' : 'bg-muted'}`}>
            3
          </div>
          <span className="text-sm font-medium">{t('merge.stepColumns')}</span>
        </div>
      </div>

      {/* Step 1: Upload second file */}
      {step === 1 && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">{t('merge.uploadSecond')}</label>
          <div
            {...getRootProps()}
            className={`relative border-2 border-dashed rounded-[var(--radius)] p-8 text-center cursor-pointer transition-colors duration-200 ${
              isDragActive ? 'border-primary bg-muted' : 'border-border hover:border-primary'
            }`}
          >
            <input {...getInputProps()} disabled={uploading} />
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <span className="text-sm text-foreground">{t('upload.uploading')}</span>
              </div>
            ) : isDragActive ? (
              <div className="flex flex-col items-center gap-2">
                <File className="h-10 w-10 text-primary" />
                <span className="text-sm text-foreground font-medium">{t('upload.drop')}</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-10 w-10 text-muted-foreground" />
                <span className="text-sm text-foreground">{t('upload.cta')}</span>
                <span className="text-xs text-muted-foreground">{t('upload.browse')}</span>
                <span className="text-xs text-muted-foreground mt-2">{t('upload.formats')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Select join columns and type */}
      {step === 2 && secondFile && (
        <div className="space-y-4">
          <div className="card p-4 bg-emerald-50 border-emerald-200">
            <p className="text-sm font-medium text-emerald-800">Second file: {secondFile.name}</p>
            <p className="text-xs text-emerald-700 mt-1">
              {secondFileInfo.rows} rows, {secondFileInfo.columns} columns
            </p>
          </div>

          <div>
            <label className="label mb-2">{t('merge.joinType')}</label>
            <select value={joinType} onChange={(e) => setJoinType(e.target.value)} className="select">
              <option value="left">{t('merge.joinLeft')}</option>
              <option value="right">{t('merge.joinRight')}</option>
              <option value="inner">{t('merge.joinInner')}</option>
              <option value="outer">{t('merge.joinOuter')}</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label mb-2">{t('merge.joinColumnLeft')}</label>
              <select 
                value={leftJoinCol} 
                onChange={(e) => setLeftJoinCol(e.target.value)} 
                className="select"
                required
              >
                <option value="">{t('email.selectColumn')}</option>
                {columns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label mb-2">{t('merge.joinColumnRight')}</label>
              <select 
                value={rightJoinCol} 
                onChange={(e) => setRightJoinCol(e.target.value)} 
                className="select"
                required
              >
                <option value="">{t('email.selectColumn')}</option>
                {secondColumns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          </div>

          {leftJoinCol && rightJoinCol && (
            <div className="card p-3 bg-blue-50 border-blue-200">
              <p className="text-xs text-blue-800">
                ℹ️ {t('merge.matchExplain', { left: leftJoinCol, right: rightJoinCol })}
              </p>
            </div>
          )}

          {report && (
            <div className="card p-4 space-y-3" data-testid="merge-plan">
              <h4 className="text-sm font-semibold">{t('merge.reportTitle')}</h4>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="font-medium">
                  {t('merge.reportMatched', { count: report.left_matched_rows })}
                </span>
                <span className={report.left_unmatched_rows > 0 ? 'text-destructive' : ''}>
                  {t('merge.reportUnmatched', { count: report.left_unmatched_rows })}
                </span>
                <span className="text-muted-foreground">
                  {t('merge.reportBlank', { count: report.left_blank_rows })}
                </span>
              </div>

              {report.left_matched_rows === 0 && (
                <p className="text-sm text-destructive" data-testid="merge-plan-nothing">
                  {t('merge.reportNothing')}
                </p>
              )}

              {report.left_unmatched_keys.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    {t('merge.reportKeysLeft', { column: leftJoinCol })}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {report.left_unmatched_keys.map((value) => (
                      <span key={value} className="badge badge-primary text-xs">{value}</span>
                    ))}
                    {report.truncated && (
                      <span className="text-xs text-muted-foreground">{t('merge.reportMore')}</span>
                    )}
                  </div>
                </div>
              )}

              {report.right_unmatched_keys.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    {t('merge.reportKeysRight')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {report.right_unmatched_keys.map((value) => (
                      <span key={value} className="badge text-xs">{value}</span>
                    ))}
                  </div>
                </div>
              )}

              {report.left_blank_rows > 0 && (
                <p className="text-xs text-muted-foreground">{t('merge.reportBlankNote')}</p>
              )}
            </div>
          )}

          <MissingChoice reasons={[(!leftJoinCol || !rightJoinCol) && t('merge.needJoinColumns')]} />

          <div className="flex gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={() => setStep(3)}
              disabled={!leftJoinCol || !rightJoinCol}
              className="btn-primary flex-1"
            >
              {t('merge.next')}
            </button>
            <button type="button" onClick={onCancel} className="btn-secondary">
              {t('form.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Select output columns */}
      {step === 3 && (
        <div className="space-y-4">
          <div className="card p-3 bg-blue-50 border-blue-200">
            <p className="text-xs text-blue-800">
              ℹ️ {t('merge.columnsExplain')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* First file columns */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label">{t('merge.firstFileColumns')}</label>
                <div className="flex gap-1">
                  <button type="button" onClick={selectAllLeft} className="text-xs text-primary hover:underline">
                    {t('merge.all')}
                  </button>
                  <span className="text-xs text-muted-foreground">|</span>
                  <button type="button" onClick={deselectAllLeft} className="text-xs text-primary hover:underline">
                    {t('merge.none')}
                  </button>
                </div>
              </div>
              <div className="card p-3 max-h-64 overflow-y-auto space-y-2">
                {columns.map(col => (
                  <label key={col} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedLeftCols.includes(col) || col === leftJoinCol}
                      onChange={() => toggleLeftCol(col)}
                      disabled={col === leftJoinCol}
                      className="h-4 w-4"
                    />
                    <span className={`text-sm ${col === leftJoinCol ? 'font-medium text-primary' : ''}`}>
                      {col}
                      {col === leftJoinCol && <span className="ml-2 text-xs">(join key)</span>}
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {selectedLeftCols.length} of {columns.length} selected
              </p>
            </div>

            {/* Second file columns */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label">{t('merge.secondFileColumns')}</label>
                <div className="flex gap-1">
                  <button type="button" onClick={selectAllRight} className="text-xs text-primary hover:underline">
                    {t('merge.all')}
                  </button>
                  <span className="text-xs text-muted-foreground">|</span>
                  <button type="button" onClick={deselectAllRight} className="text-xs text-primary hover:underline">
                    {t('merge.none')}
                  </button>
                </div>
              </div>
              <div className="card p-3 max-h-64 overflow-y-auto space-y-2">
                {secondColumns.map(col => (
                  <label key={col} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedRightCols.includes(col) || col === rightJoinCol}
                      onChange={() => toggleRightCol(col)}
                      disabled={col === rightJoinCol}
                      className="h-4 w-4"
                    />
                    <span className={`text-sm ${col === rightJoinCol ? 'font-medium text-primary' : ''}`}>
                      {col}
                      {col === rightJoinCol && <span className="ml-2 text-xs">(join key)</span>}
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {selectedRightCols.length} of {secondColumns.length} selected
              </p>
            </div>
          </div>

          <div className="flex gap-2 pt-4 border-t">
            <button type="button" onClick={() => setStep(2)} className="btn-secondary">
              {t('merge.back')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1"
              data-testid="apply-merge"
            >
              {loading ? t('merge.merging') : t('merge.apply')}
            </button>
            <button type="button" onClick={onCancel} className="btn-secondary">
              {t('form.cancel')}
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

export default MergeOperation;
